import { defineFurniture } from './types.js';
import type { FurnitureDef } from './types.js';

/**
 * Furniture for building a home: living room, bedroom, kitchen, bath, walls
 * and doorways, and the garden. Prices are in Kaki Dollars. Every item fits
 * one tile; walls and fences run along the tile's edge (rotate to turn them),
 * so a row of them makes a room divider or a garden fence.
 */
export const HOME_FURNITURE: Record<string, FurnitureDef> = {
  ...defineFurniture('seating', {
    sofa_modern: {
      label: 'Modern Sofa',
      description: 'Deep cushions for movie nights.',
      price: 450,
      solid: false,
      seat: 'facing',
      height: 0.6,
    },
    sofa_modern_grey: {
      label: 'Grey Modern Sofa',
      description: 'Deep cushions for movie nights, in charcoal grey.',
      price: 450,
      solid: false,
      seat: 'facing',
      height: 0.6,
    },
    sofa_modern_red: {
      label: 'Red Modern Sofa',
      description: 'Deep cushions for movie nights, in deep red.',
      price: 450,
      solid: false,
      seat: 'facing',
      height: 0.6,
    },
    sofa_corner: {
      label: 'Corner Sofa',
      description: 'An L-shaped corner to connect sofa pieces into a sectional. Rotate to pick which corner.',
      price: 500,
      solid: false,
      seat: 'facing',
      height: 0.6,
    },
    sofa_corner_grey: {
      label: 'Grey Corner Sofa',
      description: 'An L-shaped corner in charcoal grey. Rotate to pick which corner.',
      price: 500,
      solid: false,
      seat: 'facing',
      height: 0.6,
    },
    sofa_corner_red: {
      label: 'Red Corner Sofa',
      description: 'An L-shaped corner in deep red. Rotate to pick which corner.',
      price: 500,
      solid: false,
      seat: 'facing',
      height: 0.6,
    },
  }),
  ...defineFurniture('living', {
    floor_cushion: {
      label: 'Floor Cushion',
      description: 'A round cushion for sitting anywhere.',
      price: 90,
      solid: false,
      seat: 'free',
      height: 0.25,
    },
    wooden_bench: {
      label: 'Wooden Bench',
      description: 'Solid teak, made to last.',
      price: 140,
      solid: false,
      seat: 'facing',
      height: 0.5,
    },
    dining_chair: {
      label: 'Dining Chair',
      description: 'Pull it up to the table.',
      price: 90,
      solid: false,
      seat: 'facing',
      height: 0.5,
    },
    dining_table: {
      label: 'Dining Table',
      description: 'Big enough for the whole family.',
      price: 260,
      solid: true,
      height: 0.9,
    },
    side_table: {
      label: 'Side Table',
      description: 'Just right for a cup of teh.',
      price: 120,
      solid: true,
      height: 0.7,
    },
    tv_stand: {
      label: 'TV and Stand',
      description: 'Catch the National Day Parade.',
      price: 380,
      solid: true,
      height: 1.3,
    },
    piano: {
      label: 'Upright Piano',
      description: 'Plays itself, if you squint.',
      price: 1200,
      solid: true,
      height: 1.6,
    },
    fireplace: {
      label: 'Fireplace',
      description: 'A crackling fire for rainy evenings.',
      price: 900,
      solid: true,
      height: 1.6,
      effect: { type: 'flames', color: '#ff8a2a', color2: '#ffe27a', height: 12 },
    },
    grandfather_clock: {
      label: 'Grandfather Clock',
      description: 'Tick, tock, tick, tock.',
      price: 700,
      solid: true,
      height: 2.4,
    },
    aquarium: {
      label: 'Aquarium',
      description: 'Goldfish, bubbles and a tiny castle.',
      price: 850,
      solid: true,
      height: 1.3,
      effect: { type: 'glow', color: '#5bc8f0', height: 26 },
    },
    easel: {
      label: 'Painting Easel',
      description: 'A work in progress.',
      price: 180,
      solid: true,
      height: 1.7,
    },
    floor_vase: {
      label: 'Floor Vase',
      description: 'Tall, blue and white.',
      price: 110,
      solid: true,
      height: 1.2,
    },
    standing_mirror: {
      label: 'Standing Mirror',
      description: 'Check the outfit before heading out.',
      price: 220,
      solid: true,
      height: 1.9,
    },
    round_rug: {
      label: 'Round Rug',
      description: 'A soft cream rug with a blue ring.',
      price: 110,
      solid: false,
      height: 0,
    },
    plain_rug: {
      label: 'Woven Rug',
      description: 'Simple stripes, easy to match.',
      price: 90,
      solid: false,
      height: 0,
    },
  }),
  ...defineFurniture('bedroom', {
    bed_single: {
      label: 'Single Bed',
      description: 'Fresh sheets and a fluffy pillow.',
      price: 350,
      solid: true,
      height: 0.8,
    },
    bed_single_white: {
      label: 'White Single Bed',
      description: 'Fresh white sheets and a fluffy pillow.',
      price: 350,
      solid: true,
      height: 0.8,
    },
    bed_single_grey: {
      label: 'Grey Single Bed',
      description: 'Fresh grey sheets and a fluffy pillow.',
      price: 350,
      solid: true,
      height: 0.8,
    },
    bed_frameless: {
      label: 'Frameless Bed',
      description: 'A low mattress straight on the floor. No headboard, no fuss.',
      price: 220,
      solid: true,
      height: 0.35,
    },
    bed_frameless_white: {
      label: 'White Frameless Bed',
      description: 'A low mattress straight on the floor, in fresh white. No headboard, no fuss.',
      price: 220,
      solid: true,
      height: 0.35,
    },
    bunk_bed: {
      label: 'Bunk Bed',
      description: 'Top bunk goes to whoever calls it first.',
      price: 520,
      solid: true,
      height: 1.8,
    },
    wardrobe: {
      label: 'Wardrobe',
      description: 'Room for every shirt you designed.',
      price: 420,
      solid: true,
      height: 2,
    },
    dresser: {
      label: 'Dresser',
      description: 'Three drawers, one always stuck.',
      price: 260,
      solid: true,
      height: 1,
    },
    nightstand: {
      label: 'Nightstand',
      description: 'A lamp and a glass of water.',
      price: 110,
      solid: true,
      height: 0.8,
    },
    teddy_bear: {
      label: 'Teddy Bear',
      description: 'Very good at hugs.',
      price: 60,
      solid: true,
      height: 0.6,
    },
  }),
  ...defineFurniture('kitchen', {
    fridge: {
      label: 'Fridge',
      description: 'Magnets from every trip.',
      price: 500,
      solid: true,
      height: 2,
    },
    stove: {
      label: 'Stove',
      description: 'Two burners and a wok.',
      price: 420,
      solid: true,
      height: 1,
    },
    kitchen_counter: {
      label: 'Kitchen Counter',
      description: 'Chop, slice, repeat.',
      price: 220,
      solid: true,
      height: 1,
    },
    kitchen_sink: {
      label: 'Kitchen Sink',
      description: 'Always a stack of bowls.',
      price: 300,
      solid: true,
      height: 1,
    },
    pantry: {
      label: 'Pantry Cabinet',
      description: 'Instant noodles, sauces and more instant noodles.',
      price: 300,
      solid: true,
      height: 2,
    },
    water_dispenser: {
      label: 'Water Dispenser',
      description: 'Hot or cold, your choice.',
      price: 200,
      solid: true,
      height: 1.3,
    },
  }),
  ...defineFurniture('bath', {
    bathtub: {
      label: 'Bathtub',
      description: 'Bubbles all the way up.',
      price: 480,
      solid: true,
      height: 0.8,
    },
    toilet: {
      label: 'Toilet',
      description: 'Every home needs one.',
      price: 200,
      solid: false,
      seat: 'free',
      height: 0.6,
    },
    washbasin: {
      label: 'Washbasin',
      description: 'A pedestal sink with a mirror.',
      price: 220,
      solid: true,
      height: 1.1,
    },
    shower: {
      label: 'Shower Stall',
      description: 'Glass doors and a rain head.',
      price: 400,
      solid: true,
      height: 2.2,
    },
    washing_machine: {
      label: 'Washing Machine',
      description: 'Front loader with a round window.',
      price: 380,
      solid: true,
      height: 1,
    },
    laundry_rack: {
      label: 'Laundry Rack',
      description: 'Clothes dry in the Singapore sun.',
      price: 130,
      solid: true,
      height: 1.4,
    },
  }),
  ...defineFurniture('decor', {
    arc_lamp: {
      label: 'Arc Floor Lamp',
      description: 'Reaches right over the sofa.',
      price: 240,
      solid: true,
      height: 2,
    },
    table_lamp: {
      label: 'Table Lamp',
      description: 'A warm little glow.',
      price: 80,
      solid: true,
      height: 0.7,
    },
    candles: {
      label: 'Candle Stand',
      description: 'Three candles, no cake.',
      price: 90,
      solid: true,
      height: 0.9,
      effect: { type: 'flames', color: '#ffb84a', color2: '#fff2b0', height: 24 },
    },
    paper_lantern: {
      label: 'Paper Lantern',
      description: 'Red and gold, on a bamboo pole.',
      price: 130,
      solid: true,
      height: 1.8,
      effect: { type: 'glow', color: '#ff6a4a', height: 40 },
    },
  }),
  ...defineFurniture('build', {
    wall_low: {
      label: 'Low Wall',
      description: 'Knee-high divider. Rotate to change direction.',
      price: 60,
      solid: true,
      height: 0.7,
    },
    wall_high: {
      label: 'Room Wall',
      description: 'A full wall to make a room inside your room. Rotate to change direction.',
      price: 120,
      solid: true,
      height: 2.4,
    },
    wall_window: {
      label: 'Window Wall',
      description: 'A wall with a window to let the light in. Rotate to change direction.',
      price: 160,
      solid: true,
      height: 2.4,
    },
    doorway: {
      label: 'Doorway',
      description: 'A wall with an opening you can walk through. Rotate to change direction.',
      price: 100,
      solid: false,
      height: 2.4,
    },
    door_wood: {
      label: 'Wooden Door',
      description: 'A proper door with a handle. Click it to open or close it. Rotate to change direction.',
      price: 110,
      solid: true,
      height: 2.2,
      interaction: { type: 'door' },
    },
    door_glass: {
      label: 'Glass Door',
      description: 'See-through and swings open. Click it to open or close it. Rotate to change direction.',
      price: 150,
      solid: true,
      height: 2.2,
      interaction: { type: 'door' },
    },
    door_metal: {
      label: 'Steel Door',
      description: 'A heavy sliding door. Click it to open or close it. Rotate to change direction.',
      price: 180,
      solid: true,
      height: 2.2,
      interaction: { type: 'door' },
    },
    pillar: {
      label: 'Pillar',
      description: 'A round column for grand entrances.',
      price: 80,
      solid: true,
      height: 2.4,
    },
    glass_wall: {
      label: 'Glass Wall',
      description: 'See through, walk around. Rotate to change direction.',
      price: 140,
      solid: true,
      height: 2.2,
    },
    wall_corner: {
      label: 'Corner Wall',
      description: 'Turns a wall run around a corner. Rotate to pick which corner.',
      price: 120,
      solid: true,
      height: 2.4,
    },
    glass_corner: {
      label: 'Glass Corner',
      description: 'A see-through corner to match the Glass Wall. Rotate to pick which corner.',
      price: 160,
      solid: true,
      height: 2.2,
    },
    wall_high_white: {
      label: 'Room Wall (White)',
      description: 'A full wall painted white. Rotate to change direction.',
      price: 120,
      solid: true,
      height: 2.4,
    },
    wall_high_pastel: {
      label: 'Room Wall (Pastel)',
      description: 'A full wall in a soft pastel blue. Rotate to change direction.',
      price: 120,
      solid: true,
      height: 2.4,
    },
    half_wall: {
      label: 'Half Wall',
      description: 'A counter-height divider, taller than the Low Wall but open on top. Rotate to change direction.',
      price: 90,
      solid: true,
      height: 1.2,
    },
    wall_shelf: {
      label: 'Shelf Wall',
      description: 'A full wall with a built-in display nook. Rotate to change direction.',
      price: 140,
      solid: true,
      height: 2.4,
    },
    curtain_doorway: {
      label: 'Curtain Doorway',
      description: 'An opening hung with a beaded curtain. Rotate to change direction.',
      price: 90,
      solid: false,
      height: 2.2,
    },
    window_round: {
      label: 'Porthole Wall',
      description: 'A wall with a round porthole window. Rotate to change direction.',
      price: 150,
      solid: true,
      height: 2.4,
    },
    wall_arch_window: {
      label: 'Arched Window Wall',
      description: 'A wall with an arched window to let the light in. Rotate to change direction.',
      price: 160,
      solid: true,
      height: 2.4,
    },
    pillar_square: {
      label: 'Square Pillar',
      description: 'A square column for grand entrances.',
      price: 80,
      solid: true,
      height: 2.4,
    },
    room_divider_screen: {
      label: 'Folding Screen',
      description: 'A freestanding screen to divide a room without a full wall. Rotate to change direction.',
      price: 110,
      solid: true,
      height: 1.6,
    },
    balcony_railing: {
      label: 'Balcony Railing',
      description: 'A waist-high railing with balusters, for balconies and upper floors. Rotate to change direction.',
      price: 100,
      solid: true,
      height: 1.0,
    },
  }),
  ...defineFurniture('garden', {
    garden_lawn: {
      label: 'Lawn Tile',
      description: 'Lush green grass. Walk right over it.',
      price: 30,
      solid: false,
      height: 0,
    },
    garden_path: {
      label: 'Stepping Stones',
      description: 'A stone path through the garden.',
      price: 40,
      solid: false,
      height: 0,
    },
    garden_deck: {
      label: 'Wooden Deck',
      description: 'Warm timber boards for the patio.',
      price: 60,
      solid: false,
      height: 0,
    },
    fence_wood: {
      label: 'Wooden Fence',
      description: 'A picket fence. Rotate to change direction.',
      price: 40,
      solid: true,
      height: 1,
    },
    fence_white: {
      label: 'White Fence',
      description: 'A tidy white fence. Rotate to change direction.',
      price: 50,
      solid: true,
      height: 1,
    },
    fence_wood_corner: {
      label: 'Brown Corner Fence',
      description: 'Turns a wooden fence run around a corner. Rotate to pick which corner.',
      price: 50,
      solid: true,
      height: 1,
    },
    fence_white_corner: {
      label: 'White Corner Fence',
      description: 'Turns a white fence run around a corner. Rotate to pick which corner.',
      price: 60,
      solid: true,
      height: 1,
    },
    hedge: {
      label: 'Hedge',
      description: 'Neatly trimmed.',
      price: 70,
      solid: true,
      height: 1.1,
    },
    flower_bed: {
      label: 'Flower Bed',
      description: 'Bougainvillea and hibiscus in bloom.',
      price: 90,
      solid: true,
      height: 0.4,
    },
    frangipani: {
      label: 'Frangipani Tree',
      description: 'White flowers, sweet smell.',
      price: 200,
      solid: true,
      height: 2.4,
    },
    palm_tree: {
      label: 'Coconut Palm',
      description: 'Tall, swaying and very Singapore.',
      price: 300,
      solid: true,
      height: 2.4,
    },
    garden_bench: {
      label: 'Garden Bench',
      description: 'Sit and watch the plants grow.',
      price: 150,
      solid: false,
      seat: 'facing',
      height: 0.5,
    },
    pond: {
      label: 'Koi Pond',
      description: 'Orange koi and lily pads.',
      price: 350,
      solid: true,
      height: 0.3,
    },
    garden_lamp: {
      label: 'Garden Lamp',
      description: 'Lights the path at night.',
      price: 120,
      solid: true,
      height: 1.6,
    },
    bbq_pit: {
      label: 'BBQ Pit',
      description: 'Satay, sausages and good company.',
      price: 260,
      solid: true,
      height: 0.9,
      effect: { type: 'flames', color: '#ff7a2a', color2: '#ffd84a', height: 14 },
    },
    hammock: {
      label: 'Hammock',
      description: 'Swing gently between two posts.',
      price: 320,
      solid: false,
      seat: 'free',
      height: 0.5,
    },
    umbrella_table: {
      label: 'Umbrella Table',
      description: 'Shade for tea time.',
      price: 280,
      solid: true,
      height: 2,
    },
    garden_gnome: {
      label: 'Garden Gnome',
      description: 'Guards the flowers, mostly.',
      price: 90,
      solid: true,
      height: 0.8,
    },
    bird_bath: {
      label: 'Bird Bath',
      description: 'Sparrows love it.',
      price: 160,
      solid: true,
      height: 1,
    },
    wishing_well: {
      label: 'Wishing Well',
      description: 'Toss a coin and make a wish.',
      price: 450,
      solid: true,
      height: 1.4,
      effect: { type: 'sparkle', color: '#bfe8ff', height: 22 },
    },
    scarecrow: {
      label: 'Scarecrow',
      description: 'Keeps the crows off. Wears a straw hat.',
      price: 180,
      solid: true,
      height: 1.8,
    },
  }),
  ...defineFurniture('farm', {
    garden_plot: {
      label: 'Garden Plot',
      description:
        'Plant seeds, water them every day and harvest. Crops only grow on days you water them. Sell your harvest for K$.',
      price: 150,
      solid: false,
      height: 0.1,
      interaction: { type: 'garden' },
    },
    raised_bed: {
      label: 'Raised Bed',
      description: 'A timber bed with richer soil. Harvests sell for 25% more.',
      price: 400,
      solid: true,
      height: 0.35,
      interaction: { type: 'garden' },
    },
    greenhouse_bed: {
      label: 'Greenhouse Bed',
      description: 'A glass-covered bed. Harvests sell for 50% more.',
      price: 900,
      solid: true,
      height: 1.2,
      interaction: { type: 'garden' },
    },
    chicken_coop: {
      label: 'Chicken Coop',
      description: 'Feed the hens every day and collect fresh eggs.',
      price: 500,
      solid: true,
      height: 1.1,
      interaction: { type: 'garden' },
    },
    beehive: {
      label: 'Beehive',
      description: 'Bees make honey every few days if you keep them fed.',
      price: 700,
      solid: true,
      height: 1,
      interaction: { type: 'garden' },
    },
    tool_shed: {
      label: 'Tool Shed',
      description: 'Spades, hoes and a very old radio.',
      price: 500,
      solid: true,
      height: 2,
    },
    wheelbarrow: {
      label: 'Wheelbarrow',
      description: 'Full of soil and good intentions.',
      price: 140,
      solid: true,
      height: 0.7,
    },
    hay_bale: {
      label: 'Hay Bale',
      description: 'Itchy but comfy.',
      price: 60,
      solid: false,
      seat: 'free',
      height: 0.5,
    },
    water_barrel: {
      label: 'Rain Barrel',
      description: 'Catches the afternoon downpour.',
      price: 120,
      solid: true,
      height: 1,
    },
  }),
};
